/**
 * Learning organized crimes (owner's call 2026-10-20: "Start gathering the data … we can get a fair jumpstart by
 * using tornprobability"). Finished crimes are kept as anonymous samples — the scenario, each role's pass rate and
 * whether the crime succeeded; no players, no faction — and, per scenario, a small model learns how much each role's
 * pass rate moves the chance of success: a logistic regression that starts from tornprobability.com's role weights
 * (its prior) and moves away from them only as far as the data says. Pure; shared by the server (which gathers and
 * fits) and the extension (which uses the result).
 */

type Obj = Record<string, unknown>;
const obj = (x: unknown): Obj | null => (x && typeof x === 'object' && !Array.isArray(x) ? (x as Obj) : null);
const num = (x: unknown): number | null => (typeof x === 'number' && Number.isFinite(x) ? x : null);

/** "Break the Bank" → "breakthebank", "Muscle #2" → "muscle" (a crime's key; a role without its seat number). */
export const ocKey = (name: string): string => name.toLowerCase().replace(/\s*#\d+$/, '').replace(/&/g, 'and').replace(/[^a-z0-9]/g, '');

/**
 * A seat's key: "Muscle #2" → "muscle2", "Robber" → "robber" — the way tornprobability keys its role weights (report
 * 175644-002a: Thief #1 weighs 3% of Break the Bank and Thief #2 29%, so seats of the same role are learned apart).
 */
export const seatKey = (name: string): string => name.toLowerCase().replace(/&/g, 'and').replace(/[^a-z0-9]/g, '');

/** A seat's weight in its crime's weights: its own ("muscle2"), else its role's ("muscle", older merged keys). */
export function seatWeight(crimeWeights: Record<string, number> | null | undefined, role: string): number | null {
  const w = crimeWeights?.[seatKey(role)] ?? crimeWeights?.[ocKey(role)];
  return typeof w === 'number' && Number.isFinite(w) ? w : null;
}

/**
 * Each slot's seat name as Torn writes it on the crime ("Muscle #2"): the API's `position` when it carries the number,
 * else its `position_info.label`, else the role plus its number when the role is in the crime more than once.
 */
export function seatNames(slots: ReadonlyArray<Record<string, unknown>>): string[] {
  const base = slots.map((s) => String(obj(s.position_info)?.label ?? s.position ?? '').trim());
  const role = (b: string) => b.replace(/\s*#\d+$/, '');
  const count = new Map<string, number>();
  for (const b of base) count.set(role(b), (count.get(role(b)) ?? 0) + 1);
  return slots.map((s, i) => {
    const b = base[i]!;
    if (/#\d+$/.test(b) || (count.get(role(b)) ?? 0) < 2) return b;
    const n = num(obj(s.position_info)?.number) ?? num(s.position_number);
    return n !== null ? `${b} #${n}` : b;
  });
}

export interface OcSample {
  /** Torn's crime id (only to keep each crime once). */
  id: number;
  scenario: string;
  /** As Torn writes it ("Break the Bank"). */
  name: string;
  difficulty: number | null;
  success: boolean;
  /** Unix seconds it ran. */
  at: number;
  /** Each seat: its seat key ("muscle2"; older samples: the role, "muscle") and the pass rate (0–100) of whoever filled it. */
  roles: Array<{ role: string; rate: number }>;
}

/** v2 /faction/crimes?cat=completed → samples (finished ones with every seat's pass rate known). No players kept. */
export function samplesFromCrimes(body: unknown): OcSample[] {
  const list = obj(body)?.crimes;
  const out: OcSample[] = [];
  for (const raw of Array.isArray(list) ? list : Object.values(obj(list) ?? {})) {
    const c = obj(raw);
    const id = num(c?.id);
    const status = String(c?.status ?? '');
    if (!c || !id || !/^(successful|failure|failed)$/i.test(status)) continue;
    const slots = Array.isArray(c.slots) ? c.slots.map(obj).filter((s): s is Obj => !!s) : [];
    const names = seatNames(slots);
    const roles = slots.map((s, i) => ({ role: seatKey(names[i]!), rate: num(s.checkpoint_pass_rate) ?? num(obj(s.user)?.checkpoint_pass_rate) }));
    if (!roles.length || roles.some((r) => !r.role || r.rate === null || r.rate < 0 || r.rate > 100)) continue;
    out.push({ id, scenario: ocKey(String(c.name ?? '')), name: String(c.name ?? '').slice(0, 60), difficulty: num(c.difficulty), success: /^successful$/i.test(status), at: num(c.executed_at) ?? num(c.ready_at) ?? 0, roles: roles as OcSample['roles'] });
  }
  return out;
}

/** Only well-formed samples (from a client or a file). */
export function cleanSamples(x: unknown, max = 200): OcSample[] {
  const out: OcSample[] = [];
  for (const raw of Array.isArray(x) ? x.slice(0, max) : []) {
    const s = obj(raw);
    const id = num(s?.id);
    const roles = Array.isArray(s?.roles) ? (s!.roles as unknown[]).map(obj).filter((r): r is Obj => !!r).map((r) => ({ role: seatKey(String(r.role ?? '')), rate: num(r.rate) })) : [];
    if (!s || !id || id <= 0 || typeof s.scenario !== 'string' || typeof s.success !== 'boolean' || !roles.length || roles.length > 10 || roles.some((r) => !r.role || r.rate === null || r.rate < 0 || r.rate > 100)) continue;
    out.push({ id, scenario: ocKey(s.scenario).slice(0, 60), name: (typeof s.name === 'string' ? s.name : s.scenario).slice(0, 60), difficulty: num(s.difficulty), success: s.success, at: num(s.at) ?? 0, roles: roles as OcSample['roles'] });
  }
  return out;
}

export interface OcModel {
  scenario: string;
  /** As Torn writes it, when a crime of it was seen. */
  name: string;
  /** Crimes it learned from. */
  n: number;
  successRate: number;
  /** How much each seat matters, scaled to add up to 100 like tornprobability's (prior alone when there's little data). */
  weights: Record<string, number>;
  /** The fitted model: P(success) = σ(bias + Σ coef[seat] × (rate − 70) / 10). */
  bias: number;
  coef: Record<string, number>;
  /** "prior" (tornprobability only), "blend", or "learned" (enough data that it leads). */
  basis: 'prior' | 'blend' | 'learned';
}

/** Pass rates are measured from here, in steps of 10 points, in the model. */
export const OC_CENTER = 70;
/** The prior's coefficients add up to this: a crime's whole line-up 10 points better moves its odds about e². */
export const OC_PRIOR_SCALE = 2;
/** How many crimes' worth of evidence the prior (tornprobability's weights) counts for, per seat. */
const PRIOR_STRENGTH = 10;

const sigmoid = (z: number) => 1 / (1 + Math.exp(-z));
const logLik = (p: number, y: number) => Math.log(Math.max(1e-12, y ? p : 1 - p));

/** Solves A·x = b (small, symmetric positive definite here) — Gaussian elimination with partial pivoting. */
function solve(a: number[][], b: number[]): number[] | null {
  const n = b.length;
  const m = a.map((row, i) => [...row, b[i]!]);
  for (let c = 0; c < n; c++) {
    let p = c;
    for (let r = c + 1; r < n; r++) if (Math.abs(m[r]![c]!) > Math.abs(m[p]![c]!)) p = r;
    if (Math.abs(m[p]![c]!) < 1e-12) return null;
    [m[c], m[p]] = [m[p]!, m[c]!];
    for (let r = 0; r < n; r++) {
      if (r === c) continue;
      const f = m[r]![c]! / m[c]![c]!;
      if (f) for (let k = c; k <= n; k++) m[r]![k]! -= f * m[c]![k]!;
    }
  }
  return m.map((row, i) => row[n]! / row[i]!);
}

/**
 * Fit one scenario: per seat, how much its pass rate moves the crime's chance of success (report 175644-002a — seats of
 * the same role are apart: "Thief #1" and "Thief #2" can matter very differently). A logistic regression fitted
 * exactly (Newton's method) with tornprobability's weights as its prior: each seat's coefficient starts at its weight
 * share × OC_PRIOR_SCALE and the data moves it only as far as it outweighs PRIOR_STRENGTH crimes. A pass rate never
 * lowers the chance (coefficients are kept ≥ 0). Older samples that lumped seats of one role together ("muscle" twice)
 * can't tell the seats apart and are left out.
 */
export function fitScenario(scenario: string, samples: readonly OcSample[], prior: Record<string, number> | null, opts: { strength?: number } = {}): OcModel {
  const usable = samples.filter((s) => new Set(s.roles.map((r) => r.role)).size === s.roles.length);
  const roles = [...new Set([...usable.flatMap((s) => s.roles.map((r) => r.role)), ...Object.keys(prior ?? {})])].sort();
  const priorTotal = Object.values(prior ?? {}).reduce((a, b) => a + b, 0) || 1;
  const p0 = roles.map((r) => (prior?.[r] !== undefined ? (OC_PRIOR_SCALE * prior[r]!) / priorTotal : OC_PRIOR_SCALE / Math.max(1, roles.length)));
  const n = usable.length;
  const successRate = n ? usable.filter((s) => s.success).length / n : 0;
  const k = roles.length;
  // θ = [bias, coef…]; the prior: bias ~ 0 (very loose), coef ~ p0 (PRIOR_STRENGTH).
  const mean = [0, ...p0];
  const prec = [0.01, ...roles.map(() => opts.strength ?? PRIOR_STRENGTH)];
  const at = new Map(roles.map((r, j) => [r, j + 1]));
  const rows = usable.map((s) => {
    const x = new Array<number>(k + 1).fill(0);
    x[0] = 1;
    for (const r of s.roles) x[at.get(r.role)!]! += (r.rate - OC_CENTER) / 10;
    return { x, y: s.success ? 1 : 0 };
  });
  const theta = [n ? Math.log((successRate + 0.05) / (1 - successRate + 0.05)) : 0, ...p0];
  const free = theta.map(() => true);
  const objective = (t: number[]) => rows.reduce((a, { x, y }) => a + logLik(sigmoid(x.reduce((z, v, j) => z + v * t[j]!, 0)), y), 0) - t.reduce((a, v, j) => a + 0.5 * prec[j]! * (v - mean[j]!) ** 2, 0);
  const newton = () => {
    const idx = theta.map((_, j) => j).filter((j) => free[j]);
    for (let iter = 0; iter < 60; iter++) {
      const g = idx.map((j) => -prec[j]! * (theta[j]! - mean[j]!));
      const hess = idx.map((j, a) => idx.map((_, b) => (a === b ? prec[j]! : 0)));
      for (const { x, y } of rows) {
        const p = sigmoid(x.reduce((z, v, j) => z + v * theta[j]!, 0));
        const w = p * (1 - p);
        idx.forEach((j, a) => {
          g[a]! += (y - p) * x[j]!;
          idx.forEach((l, b) => (hess[a]![b]! += w * x[j]! * x[l]!));
        });
      }
      const step = solve(hess, g);
      if (!step) return;
      const before = objective(theta);
      let size = 1;
      for (let tries = 0; tries < 30; tries++, size /= 2) {
        const next = [...theta];
        idx.forEach((j, a) => (next[j] = theta[j]! + size * step[a]!));
        if (objective(next) >= before - 1e-12) {
          idx.forEach((j) => (theta[j] = next[j]!));
          break;
        }
      }
      if (Math.max(...step.map((v) => Math.abs(v * size))) < 1e-9) return;
    }
  };
  if (n) {
    // Seats whose fit comes out below 0 are held at 0 and the rest refitted.
    for (let round = 0; round <= k; round++) {
      newton();
      const neg = theta.map((v, j) => j > 0 && free[j] && v < 0);
      if (!neg.some(Boolean)) break;
      neg.forEach((x, j) => {
        if (x) {
          free[j] = false;
          theta[j] = 0;
        }
      });
    }
  }
  const coef = Object.fromEntries(roles.map((r, j) => [r, Math.max(0, theta[j + 1]!)]));
  const total = Object.values(coef).reduce((a, b) => a + b, 0) || 1;
  const weights = Object.fromEntries(roles.map((r) => [r, Math.round((coef[r]! / total) * 1000) / 10]));
  return { scenario, name: usable[0]?.name ?? samples[0]?.name ?? scenario, n, successRate: Math.round(successRate * 1000) / 1000, weights, bias: Math.round(theta[0]! * 1000) / 1000, coef: Object.fromEntries(roles.map((r) => [r, Math.round(coef[r]! * 1000) / 1000])), basis: n < 10 ? 'prior' : n < 100 ? 'blend' : 'learned' };
}

/** Every scenario in the samples (and the prior's), fitted. */
export function fitAll(samples: readonly OcSample[], priors: Record<string, Record<string, number>> | null): Record<string, OcModel> {
  const by = new Map<string, OcSample[]>();
  for (const s of samples) by.set(s.scenario, [...(by.get(s.scenario) ?? []), s]);
  const out: Record<string, OcModel> = {};
  for (const key of new Set([...by.keys(), ...Object.keys(priors ?? {})])) out[key] = fitScenario(key, by.get(key) ?? [], priors?.[key] ?? null);
  return out;
}

/** Role weights to use (the same shape as tornprobability's): learned where there's data, the prior elsewhere. */
export function modelWeights(models: Record<string, OcModel>): Record<string, Record<string, number>> {
  return Object.fromEntries(Object.entries(models).map(([k, m]) => [k, m.weights]));
}

/** The model's chance of success for a full line-up (pass rates by seat, "Muscle #2"). */
export function successChance(m: Pick<OcModel, 'bias' | 'coef'>, seats: ReadonlyArray<{ role: string; rate: number }>): number {
  let z = m.bias;
  for (const s of seats) z += (m.coef[seatKey(s.role)] ?? m.coef[ocKey(s.role)] ?? 0) * ((s.rate - OC_CENTER) / 10);
  return sigmoid(z);
}

/**
 * What a crime's learned model says about how touchy it is (for each seat's needed pass rate, oc-need.ts): crimes
 * learned from, the fitted bias and the coefficients' sum (how hard the whole line-up's pass rates move the odds).
 */
export interface OcSens {
  n: number;
  bias: number;
  scale: number;
}

export function modelSens(m: Pick<OcModel, 'n' | 'bias' | 'coef'>): OcSens {
  return { n: m.n, bias: m.bias, scale: Math.round(Object.values(m.coef).reduce((a, b) => a + Math.max(0, b), 0) * 1000) / 1000 };
}

/** Crimes the server has learned: their seat weights and how touchy they are. */
export interface OcLearned {
  weights: Record<string, Record<string, number>>;
  sens: Record<string, OcSens>;
}

/** The server's /api/oc/model answer → what the extension keeps: weights and touchiness of crimes it has learned. */
export function learnedFromModels(body: unknown): OcLearned {
  const weights: Record<string, Record<string, number>> = {};
  const sens: Record<string, OcSens> = {};
  for (const [k, raw] of Object.entries(obj(obj(body)?.models) ?? {})) {
    const m = obj(raw);
    const w = obj(m?.weights);
    if (!m || !w || !m.basis || m.basis === 'prior') continue;
    const clean = Object.fromEntries(Object.entries(w).filter(([, v]) => num(v) !== null)) as Record<string, number>;
    if (!Object.keys(clean).length) continue;
    weights[k] = clean;
    const coef = obj(m.coef);
    const n = num(m.n);
    const bias = num(m.bias);
    if (coef && n !== null && bias !== null) sens[k] = modelSens({ n, bias, coef: Object.fromEntries(Object.entries(coef).filter(([, v]) => num(v) !== null)) as Record<string, number> });
  }
  return { weights, sens };
}
